import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import AuthHero from "../../components/auth/AuthHero";
import AuthMobileLogo from "../../components/auth/AuthMobileLogo";
import { authService } from "../../services/api";

const ForgotPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSentLink, setIsSentLink] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Reset password form state (when uid and token are present)
  const [formData, setFormData] = useState({
    new_password: "",
    confirm_password: "",
  });
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const uid = searchParams.get("uid");
  const token = searchParams.get("token");

  // Check if this is a reset password flow
  const isResetPasswordFlow = uid && token;

  useEffect(() => {
    // If this is a reset password flow, clear any existing states
    if (isResetPasswordFlow) {
      setIsSentLink(false);
      setError("");
      setSuccess("");
    }
  }, [isResetPasswordFlow]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateResetForm = () => {
    if (!formData.new_password.trim()) {
      setError("Please enter a new password");
      return false;
    }

    if (formData.new_password.length < 8) {
      setError("Password must be at least 8 characters long");
      return false;
    }

    if (formData.new_password !== formData.confirm_password) {
      setError("Passwords do not match");
      return false;
    }

    return true;
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateResetForm()) {
      return;
    }

    setIsLoading(true);
    setError("");
    setSuccess("");

    try {
      const result = await authService.resetPassword({
        uid,
        token,
        new_password: formData.new_password,
      });
      
      if (result.success) {
        setSuccess("Password has been reset successfully!");
        // Redirect to login after 2 seconds
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError("Please enter your email address");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setIsLoading(true);
    setError("");
    setSuccess("");

    try {
      const result = await authService.forgotPassword({ email });
      
      if (result.success) {
        setIsSentLink(true);
        setSuccess("Password reset email has been sent to your email.");
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email.trim()) {
      setError("Please enter your email address");
      return;
    }

    setIsLoading(true);
    setError("");
    setSuccess("");

    try {
      const result = await authService.forgotPassword({ email });
      
      if (result.success) {
        setSuccess("Password reset email has been resent to your email.");
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Render reset password form when uid and token are present
  if (isResetPasswordFlow) {
    return (
      <div className="flex min-h-screen">
        <AuthHero pageName={"signin"} />
        {/* max-md:pt-28 reserves the band the centred mobile wordmark occupies
            (absolute, top-16 + h-12 = 112px) so the vertically centred form
            can never slide up underneath it on short viewports; max-md:pb-24
            keeps the form clear of the fixed support FAB bottom-right. */}
        <div className="flex-1 bg-white flex items-center justify-center relative max-md:pt-28 max-md:pb-24">
          {/* Mobile-only wordmark — the desktop logo sits in AuthHero's dark
              panel, which is hidden below md. */}
          <AuthMobileLogo />
          <div className="w-full max-w-xl px-5">
            <h2 className="text-[23.3px] leading-[28px] font-semibold text-[#111927] mb-2">
              Reset Password
            </h2>
            <p className="text-[14px] leading-[20px] text-gray-600 mb-8">
              Enter your new password below.
            </p>

            <form onSubmit={handleResetSubmit} className="space-y-6">
              <div>
                <label className="block text-[16px] font-medium text-[#111927] mb-2">
                  New Password
                </label>
                <div className="relative">
                  <input
                    placeholder="Enter new password"
                    type={showNewPassword ? "text" : "password"}
                    name="new_password"
                    value={formData.new_password}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-lg border border-[#D1D5DB] focus:outline-none focus:ring-2 focus:ring-[#2283FF] focus:border-transparent pr-12"
                    disabled={isLoading}
                    minLength={8}
                  />
                  <button
                    type="button"
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-900 hover:text-gray-700"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-5 h-5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
                        />
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-5 h-5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                    )}
                  </button>
                </div>
                <p className="text-[12px] text-gray-500 mt-1">
                  Password must be at least 8 characters long
                </p>
              </div>

              <div>
                <label className="block text-[16px] font-medium text-[#111927] mb-2">
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    placeholder="Confirm new password"
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirm_password"
                    value={formData.confirm_password}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-lg border border-[#D1D5DB] focus:outline-none focus:ring-2 focus:ring-[#2283FF] focus:border-transparent pr-12"
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-900 hover:text-gray-700"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-5 h-5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
                        />
                      </svg>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-5 h-5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="w-full">
                  <span className="text-[14px] text-[#DC2626] font-medium">
                    {error}
                  </span>
                </div>
              )}

              {/* Success Message */}
              {success && (
                <div className="w-full">
                  <span className="text-[14px] text-[#34C759] font-medium">
                    {success}
                  </span>
                </div>
              )}

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-[#2283FF] text-white px-4 py-3 rounded-lg hover:bg-[#1a6cd4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? "Resetting..." : "Reset Password"}
                </button>
              </div>

              <div className="text-center">
                <Link
                  to="/login"
                  className="text-[#2283FF] hover:text-[#1a6cd4] text-[14px]"
                >
                  Back to Login
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Render normal forgot password form
  return (
    <div className="flex min-h-screen">
      <AuthHero pageName={"signin"} />
      {/* max-md:pt-28 reserves the band the centred mobile wordmark occupies
          (absolute, top-16 + h-12 = 112px) so the vertically centred form can
          never slide up underneath it on short viewports; max-md:pb-24 keeps
          the form clear of the fixed support FAB bottom-right. */}
      <div className="flex-1 bg-white flex items-center justify-center relative max-md:pt-28 max-md:pb-24">
        {/* Mobile-only wordmark — the desktop logo sits in AuthHero's dark
            panel, which is hidden below md. */}
        <AuthMobileLogo />
        <div className="w-full max-w-xl px-5">
          <h2 className="text-[23.3px] leading-[28px] font-semibold text-[#111927] mb-2">
            Forgot Password
          </h2>
          <p className="text-[14px] leading-[20px] text-gray-600 mb-8">
            Go back to
            <Link
              to="/login"
              className="text-[#2283FF] hover:text-[#1a6cd4] ml-1"
            >
              Login
            </Link>
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-[16px] font-medium text-[#111927] mb-2">
                Registered email address
              </label>
              <input
                placeholder="Enter registered email address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-[#D1D5DB] focus:outline-none focus:ring-2 focus:ring-[#2283FF] focus:border-transparent"
                disabled={isLoading}
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="w-full">
                <span className="text-[14px] text-[#DC2626] font-medium">
                  {error}
                </span>
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div className="w-full">
                <span className="text-[14px] text-[#34C759] font-medium">
                  {success}
                </span>
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-[#2283FF] text-white px-4 py-3 rounded-lg hover:bg-[#1a6cd4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  "Sending..."
                ) : isSentLink ? (
                  "Didn't receive. Resend link"
                ) : (
                  "Send Reset Link"
                )}
              </button>
            </div>

            {/* Resend button (only show after first send) */}
            {isSentLink && !isLoading && (
              <div>
                <button
                  type="button"
                  onClick={handleResend}
                  className="w-full bg-transparent text-[#2283FF] px-4 py-3 rounded-lg border border-[#2283FF] hover:bg-[#2283FF] hover:text-white transition-colors"
                >
                  Resend Reset Link
                </button>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
